import type { RuleRender } from 'antd/es/form';
import * as v from 'valibot';

import type { AntdFormValibotSchema } from '../types';
import { parseValidationIssues } from './valibot';

export function getValue<
  O extends Record<string, unknown>, // input object
  P extends string, // path as string
>(obj: O, path: P): unknown | undefined {
  return path
    .split('.')
    .reduce((curr: unknown, key) => (curr as Record<string, unknown> | undefined)?.[key], obj as unknown);
}

export function prepareValues<T extends v.BaseSchema<any, any, any>>(
  schema: AntdFormValibotSchema<T>,
  values?: unknown,
) {
  // Ensure all nested fields are present in the returned object, even if their values are undefined
  const schemaType = schema.type;

  switch (schemaType) {
    // skip for string|number|boolean

    case 'nullish':
    case 'optional': {
      // Handle optional schemas
      return prepareValues((schema as unknown as v.OptionalSchema<any, any>).wrapped, values);
    }
    case 'array': {
      const itemSchema = (schema as unknown as v.ArraySchema<any, any>).item as v.BaseSchema<any, any, any>;
      if (Array.isArray(values)) {
        return values.map((value): unknown => prepareValues(itemSchema, value));
      }
      return values;
    }

    case 'object':
      return Object.entries((schema as unknown as v.ObjectSchema<any, any>).entries).reduce(
        (acc: Record<string, any>, [key, fieldSchema]) => {
          const fieldType = (fieldSchema as v.BaseSchema<any, any, any>).type;
          const isOptionalField = fieldType === 'optional' || fieldType === 'nullish';
          if (isOptionalField && ['', null, undefined].includes((values as Record<string, any> | undefined)?.[key])) {
            // Due to an issue with Ant Design Form, where it stores initial values during render,
            // we need to handle optional fields differently to avoid validation conflicts
            // when the field is not filled by the user. We have to delete the key if the value is undefined
            // so that Valibot can correctly apply the optional schema logic.
            delete acc[key];
            return acc;
          }
          acc[key] = prepareValues(
            fieldSchema as v.BaseSchema<any, any, any>,
            (values as Record<string, any> | undefined)?.[key],
          );
          return acc;
        },
        { ...((values as Record<string, any>) ?? {}) },
      );

    default:
      return values;
  }
}

export const createAntdFieldRule =
  <T extends v.BaseSchema<any, any, any>>(
    schema: AntdFormValibotSchema<T>,
    getExtraValues?: () => Record<string, unknown>,
  ): RuleRender =>
  ({ getFieldsValue }) => ({
    validator: (rule) =>
      // biome-ignore lint/suspicious/noAsyncPromiseExecutor: suppress
      new Promise(async (resolve, reject) => {
        const { field } = rule as { field: string };
        const values = { ...getFieldsValue(), ...getExtraValues?.() };

        const result = v.safeParse(schema, prepareValues(schema, values));

        const errors = result.issues ? parseValidationIssues(result.issues) : undefined;

        if (errors && !Array.isArray(errors)) {
          const fieldErrors = getValue(errors, field);
          if (fieldErrors) {
            reject(fieldErrors);
            return;
          }
        }

        resolve(undefined);
      }),
  });
